/**
 * 출고등록 API 클라이언트 — backend /stock-releases/* 와 1:1.
 *
 * 권한:
 *   - 등록·수정·삭제·조회: 모든 로그인 사용자
 *   - 승인·승인취소·가져간 매입 바꾸기: mgmt + admin (서버 requireStockApprover)
 */

import { api } from './api';
import type {
  StockReleaseApproveBody,
  StockReleaseApproveResult,
  StockReleaseCreateInput,
  StockReleaseMatchCandidatesResponse,
  StockReleaseRow,
  StockReleaseUnapproveResult,
  StockReleaseUpdateInput,
} from './types';

const BASE = '/stock-releases';

export async function listRecent(): Promise<StockReleaseRow[]> {
  return api.get(BASE);
}

/** 미승인(등록) 출고등록 카운트 — 재고관리 [출고검수] 버튼 뱃지용 */
export async function getPendingCount(): Promise<{ pending: number }> {
  return api.get(`${BASE}/pending-count`);
}

export async function createRelease(
  input: StockReleaseCreateInput,
): Promise<{ releaseId: number }> {
  return api.post(BASE, input);
}

export async function updateRelease(
  releaseId: number,
  input: StockReleaseUpdateInput,
): Promise<{ releaseId: number }> {
  return api.patch(`${BASE}/${releaseId}`, input);
}

export async function deleteRelease(releaseId: number): Promise<{ releaseId: number }> {
  return api.delete(`${BASE}/${releaseId}`);
}

export async function approveRelease(
  releaseId: number,
  body: StockReleaseApproveBody = {},
): Promise<StockReleaseApproveResult> {
  return api.post(`${BASE}/${releaseId}/approve`, body);
}

export async function unapproveRelease(
  releaseId: number,
): Promise<StockReleaseUnapproveResult> {
  return api.post(`${BASE}/${releaseId}/unapprove`, {});
}

/** 자유입력 출고 승인 시 영업건 매칭 후보 + 각 영업건의 매입목록 (비교 화면용)
 *  @param q 사용자 직접 검색 키워드 (매출번호/엔드유저/제안사/사업명 부분 일치) — 옵션
 */
export async function getMatchCandidates(
  releaseId: number,
  q?: string,
): Promise<StockReleaseMatchCandidatesResponse> {
  const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  return api.get(`${BASE}/${releaseId}/match-candidates${qs}`);
}

// ── 영업건 사용처 자동완성 (cross-filter) ─────────────────────────
// 출고등록 폼의 엔드유저/제안사/프로젝트명 3필드 자동완성. 다른 두 필드가
// 채워져 있으면 매칭되는 값만 반환 (sales 테이블 DISTINCT).

export interface UseForLookupParams {
  q?: string;
  endUser?: string;
  proposalCompany?: string;
  projectName?: string;
}

function buildQs(p: UseForLookupParams): string {
  const sp = new URLSearchParams();
  if (p.q?.trim()) sp.set('q', p.q.trim());
  if (p.endUser?.trim()) sp.set('endUser', p.endUser.trim());
  if (p.proposalCompany?.trim()) sp.set('proposalCompany', p.proposalCompany.trim());
  if (p.projectName?.trim()) sp.set('projectName', p.projectName.trim());
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export async function lookupEndUsers(p: UseForLookupParams = {}): Promise<string[]> {
  return api.get(`${BASE}/lookup/end-users${buildQs(p)}`);
}

export async function lookupProposalCompanies(p: UseForLookupParams = {}): Promise<string[]> {
  return api.get(`${BASE}/lookup/proposal-companies${buildQs(p)}`);
}

export async function lookupProjectNames(p: UseForLookupParams = {}): Promise<string[]> {
  return api.get(`${BASE}/lookup/project-names${buildQs(p)}`);
}

// ── 영업건 후보 + 연결 매입목록 (출고등록 미리채움) ─────────────────
// 입력된 엔드유저/제안사/프로젝트명에 정확 매칭되는 영업건 후보와
// 각 후보의 미출고 매입을 재고 품목(keyItems — 주요·비주요 모두 채울 대상)/
// 서비스·코드없음(refItems — 참고용, 납품 시 자동 출고)로 구분 반환 (2026-07-23).

export interface SalesCandidate {
  salesId: string;
  endUser: string | null;
  proposalCompany: string | null;
  projectName: string | null;
  status: string;
  contractDate: string | null;
  companyId: string;
  /** 주요재고 미출고 매입 — 출고 품목 행으로 채울 대상 */
  keyItems: { itemCode: string; itemName: string | null; qty: number }[];
  /** 비주요재고 — 참고용. 영업건 납품 시 자동 출고되므로 여기서 별도 출고 안 함 */
  refItems: { itemName: string | null; qty: number }[];
}

export async function listSalesCandidates(
  p: { salesId?: string; endUser?: string; proposalCompany?: string; projectName?: string },
): Promise<{ candidates: SalesCandidate[] }> {
  const sp = new URLSearchParams();
  if (p.salesId?.trim()) sp.set('salesId', p.salesId.trim());
  if (p.endUser?.trim()) sp.set('endUser', p.endUser.trim());
  if (p.proposalCompany?.trim()) sp.set('proposalCompany', p.proposalCompany.trim());
  if (p.projectName?.trim()) sp.set('projectName', p.projectName.trim());
  const qs = sp.toString();
  return api.get(`${BASE}/sales-candidates${qs ? `?${qs}` : ''}`);
}

// ── 임대품목 관리 (2026-05-14) ───────────────────────────────────
// 임대/기타로 출고된 미처리 항목 조회 + 회수/출고 마무리.
// 조회: 모든 로그인 사용자 / 처리: sales + admin

export interface LeaseItemRow {
  itemId: number;
  releaseId: number;
  releaseType: '임대' | '기타';
  releaseDate: string;
  /** 출고를 등록한 사용자 ID — 본인 등록건 회수 권한 판정용 */
  requesterUserId: string;
  useForText: string | null;
  itemCode: string | null;
  itemName: string | null;
  qty: number;
  purchaseIds: number[];
  approvedAt: string | null;
  approvedBy: string | null;
  approverName: string | null;
  /** 처리 시각 — null 이면 미처리 (회수/출고 가능) */
  processedAt: string | null;
  /** 처리 종류 — null=미처리 / recovered=회수완료 / finalized=출고처리 완료 */
  processedAction: 'recovered' | 'finalized' | null;
}

export async function listLeaseItems(): Promise<LeaseItemRow[]> {
  return api.get(`${BASE}/lease-items`);
}

export async function recoverLeaseItem(
  itemId: number,
  recoveryDate: string,
): Promise<{ itemId: number; action: 'recovered' }> {
  return api.post(`${BASE}/lease-items/${itemId}/recover`, { recoveryDate });
}

export async function finalizeLeaseItem(
  itemId: number,
  finalizeDate: string,
  note?: string,
): Promise<{ itemId: number; action: 'finalized' }> {
  return api.post(`${BASE}/lease-items/${itemId}/finalize`, { finalizeDate, note });
}

// ── 승인 전 점검 · 가져간 매입 바꾸기 (v2.241.0) ─────────────────────
// 승인이 이 영업건 몫 매입(입고일 없음·규격 다름)을 건너뛰고 일반 재고를 대신 빼는지 미리 알리고,
// 이미 승인된 영업건 출고는 가져간 매입을 이 영업건 매입으로 바꿔 끼운다. 권한: 관리팀·관리자.

export interface ApproveCheckWarning {
  itemId: number;
  itemCode: string;
  itemName: string | null;
  itemSpec: string | null;
  qty: number;
  /** 이 영업건 매입 중 이번 출고가 실제로 가져갈 수 있는 수량 */
  eligibleReserved: number;
  /** 입고일이 비어 빠지는 수량 */
  notIn: number;
  /** 규격 글자가 달라 빠지는 수량과 그 규격들 */
  specDiff: number;
  diffSpecs: string[];
  /** 대신 일반 재고에서 나갈 수량 */
  fromGeneral: number;
  /** 일반 재고 가용 수량 — 모자라면 승인이 거절된다 */
  generalAvail: number;
}

/** salesId — 승인 창에서 고른 영업건(등록 때 영업건을 골랐으면 서버가 그것을 먼저 쓴다) */
export async function approveCheck(
  releaseId: number,
  salesId?: string | null,
): Promise<{ salesId: string | null; warnings: ApproveCheckWarning[] }> {
  const qs = salesId ? `?salesId=${encodeURIComponent(salesId)}` : '';
  return api.get(`${BASE}/${releaseId}/approve-check${qs}`);
}

/** 승인 확인 창에 붙일 경고 글 — 없으면 '' (출고검수 빠른 승인 · 영업건 연결 창이 같이 쓴다) */
export function approveCheckText(salesId: string | null, warnings: ApproveCheckWarning[]): string {
  if (warnings.length === 0) return '';
  const lines = warnings.map((w) => {
    const name = w.itemName ?? w.itemCode;
    const why = [
      w.notIn > 0 ? `입고일 없음 ${w.notIn}개` : '',
      w.specDiff > 0
        ? `규격 다름 ${w.specDiff}개(${w.diffSpecs.map((s) => `「${s}」`).join(', ') || '규격 없음'} ≠ 출고등록 「${w.itemSpec ?? ''}」)`
        : '',
    ].filter(Boolean).join(' · ');
    const tail = w.generalAvail >= w.fromGeneral
      ? `→ 일반 재고에서 ${w.fromGeneral}개가 대신 나갑니다`
      : `→ 일반 재고도 ${w.generalAvail}개뿐이라 승인이 거절됩니다`;
    return `  • ${name} ×${w.qty} — 이 영업건 매입 중 ${why} ${tail}`;
  });
  return (
    `⚠ 이 영업건${salesId ? `(#${salesId})` : ''} 몫으로 산 매입이 이번 출고에서 빠집니다\n` +
    lines.join('\n') + '\n' +
    `실제로 나간 것이 이 영업건 매입이면 [취소]를 누르고, 매입관리에서 입고일을 넣거나\n` +
    `출고등록의 규격을 이 영업건 매입 규격으로 고친 뒤 승인해 주세요.\n` +
    `(이미 승인한 뒤라면 영업건 상세 › 매입 탭의 [🔁 가져간 매입 바꾸기]로 고칠 수 있습니다)\n\n`
  );
}

export interface SalesReleaseSummary {
  releaseId: number;
  releaseDate: string;
  /** 재고 품목을 실제로 가져간 기록이 있는 품목이 하나라도 있나 */
  swappable: boolean;
  items: { itemCode: string | null; name: string; itemSpec: string | null; qty: number }[];
}

/** 그 영업건의 승인된 영업건 출고 — 영업건 상세 매입 탭 단추용 */
export async function listBySales(salesId: string): Promise<SalesReleaseSummary[]> {
  return api.get(`${BASE}/by-sales/${encodeURIComponent(salesId)}`);
}

export interface SwapLine {
  purchaseId: number;
  description: string | null;
  supplier: string | null;
  orderQty: number;
  unitPrice: number | null;
  amount: number | null;
  actualInDate: string | null;
  actualOutDate: string | null;
  notePreview: string | null;
}

export interface SwapItem {
  itemId: number;
  itemCode: string | null;
  name: string;
  itemSpec: string | null;
  qty: number;
  /** 이 품목은 바꿀 수 없는 이유 (나눠 가져감·반품 처리 등) */
  blockedReason: string | null;
  current: (SwapLine & { returnBlockedReason: string | null; staysLinked: boolean })[];
  candidates: SwapLine[];
  unavailable: (SwapLine & { reason: string })[];
}

export interface SwapOptions {
  releaseId: number;
  releaseDate: string;
  salesId: string | null;
  status: string;
  releaseType: string;
  /** 출고 전체를 바꿀 수 없는 이유 (승인 아님·영업건 아님·판보 확정) */
  blockedReason: string | null;
  items: SwapItem[];
}

export async function getSwapOptions(releaseId: number): Promise<SwapOptions> {
  return api.get(`${BASE}/${releaseId}/swap-options`);
}

export interface SwapResult {
  releaseId: number;
  itemId: number;
  salesId: string;
  returned: number[];
  taken: number[];
  qty: number;
  /** 되돌린 뒤에도 이 영업건 연결이 남은 줄 */
  stillLinked: number[];
}

export async function swapItem(
  releaseId: number,
  itemId: number,
  body: { returnIds: number[]; takeIds: number[] },
): Promise<SwapResult> {
  return api.post(`${BASE}/${releaseId}/items/${itemId}/swap`, body);
}
